// threeOptions.js — point 2 of the shipping plan (owner, 2026-11-02): every product offers
// sea, air and express. The carrier is chosen behind the scenes and never shown:
//   sea / air — the partner for the products it accepts; for the ones it refuses
//               (market_delivered.partner_refused) the cheaper of the refused-rate rows
//               (market_public_state().delivered.refused_rates — HioBuy / DHL Global
//               Forwarding, by class, the names never leave the server);
//   express   — the server's quote (the cheaper of HioBuy Express and DHL Express).
// A product with no rate for an option is listed in that option's `ask` («اطلب سعر الشحن»)
// and never blocks the option for the others. No estimate is ever produced: a line is
// priced from a real rate or it asks.
//
// ONE file, byte-copied — web src/lib/threeOptions.js, server
// supabase/functions/market-checkout/threeOptions.js, app src/lib/market/threeOptions.js.
// A test in each repo asserts the copies are identical. Settings use the keys of
// market_public_state().delivered (+ fee_tiers); the server maps its own onto them
// (delivered.ts publicSettings). Pure JavaScript, no imports.

const r2 = (n) => Math.round(n * 100) / 100;
export const MAX_EXPRESS_UNIT_KG = 30;
export const MODES = ['sea', 'air', 'express'];

/** Rounding up (owner, 2026-11-02) — the same as marketDelivered.js / delivered.ts roundPrice. */
export function roundPrice(p) {
  const c = Math.round(Number(p) * 100);
  if (!(c > 0) || c % 100 === 0) return r2(p);
  if (c < 1000) return Math.ceil(c / 5) * 5 / 100;
  const n = Math.floor(c / 100), f = c - n * 100;
  if (c <= 10000) return f <= 45 ? r2(n + 0.45) : f <= 95 ? r2(n + 0.95) : n + 1 < 100 ? r2(n + 1.45) : 100.95;
  return f <= 95 ? r2(n + 0.95) : r2(n + 1.95);
}
const shown = (p, s) => (s.price_rounding ? roundPrice(p) : r2(p));
const allowance = (s) => Number(s.payment_allowance) || 0;
/** The product price (goods + fee + allowance, rounded as shown) — no shipping. */
export function productPrice(unitSar, s) {
  const u = Number(unitSar);
  return shown(u + u * Number(s.fee_rate) + u * allowance(s), s);
}
function airRate(cls, s) {
  return Number(cls === 'sensitive' ? s.air_rate_sensitive : cls === 'electrical' ? s.air_rate_electrical : s.air_rate_general);
}
function seaRate(cls, s) {
  const by = cls === 'sensitive' ? s.sea_rate_sensitive : cls === 'electrical' ? s.sea_rate_electrical : null;
  return Number(by) > 0 ? Number(by) : Number(s.sea_rate_cbm);
}
/** Per unit: chargeable kg by air, m³ by sea (bulk / nesting rules; sea never under weight ÷ sea_kg_per_cbm). */
export function unitMeasures(sku, qty, s) {
  const kg = Number(sku.kg), cm3 = Number(sku.cm3);
  const pf = Number(s.packaging_factor), div = Number(s.volumetric_divisor);
  const ratio = kg > 0 ? (cm3 / div) / (kg * pf) : Infinity;
  const atBulkQty = qty >= Number(s.bulk_min_qty);
  const bulk = atBulkQty && ratio <= Number(s.bulk_max_ratio);
  const nf = Number(s.nesting_factor);
  const nest = !bulk && atBulkQty && sku.nests === true && nf > 0;
  const vf = bulk ? Number(s.bulk_volume_factor) : nest ? nf : 1;
  const kpc = Number(s.sea_kg_per_cbm);
  return { air_kg: Math.max(kg * pf, (cm3 / div) * vf), sea_cbm: Math.max((cm3 / 1e6) * vf, kpc > 0 ? (kg * pf) / kpc : 0) };
}
/** One line landed by air or sea at the settings' rates: the shown unit × qty with rounding on (the cart equals the card). */
function lineLanded(l, mode, s) {
  const u = unitMeasures(l.sku, l.qty, s);
  const ship = mode === 'air' ? u.air_kg * airRate(l.cls, s) : u.sea_cbm * seaRate(l.cls, s);
  const fee = l.unit_sar * Number(s.fee_rate) + l.unit_sar * allowance(s);
  if (s.price_rounding) return r2(shown(l.unit_sar + ship + fee, s) * l.qty);
  const goods = l.unit_sar * l.qty;
  return r2(goods + r2(ship * l.qty) + goods * Number(s.fee_rate) + goods * allowance(s));
}
/** Rule B: air does not suit a variant (sea-only goods, or air freight > half its air price at > 30 kg). */
export function airUnsuited(sku, cls, unitSar, s) {
  if (sku?.air_ok === false) return true;
  if (!sku || !(Number(unitSar) > 0)) return false;
  const u = unitMeasures(sku, 1, s);
  const freight = u.air_kg * airRate(cls, s);
  const air = shown(Number(unitSar) + freight + Number(unitSar) * Number(s.fee_rate) + Number(unitSar) * allowance(s), s);
  return u.air_kg > 30 && freight > 0.5 * air;
}
const ladder = (s) => (Array.isArray(s.fee_tiers) && s.fee_tiers.length ? s.fee_tiers : [{ from_sar: 0, rate: Number(s.fee_rate) || 0 }]);
function feeRateFor(base, s) {
  let rate = ladder(s)[0].rate;
  for (const t of ladder(s)) if (base >= t.from_sar) rate = t.rate;
  return Number(rate);
}
function nextTier(base, s) {
  const t = ladder(s).find((x) => x.from_sar > base);
  return t ? { from_sar: t.from_sar, rate: Number(t.rate), need_sar: r2(t.from_sar - base) } : null;
}
/** Pieces to add to one line until the lines reach the sea minimum (the bulk rule counted at the new quantity). */
function piecesToSeaMin(lines, seaCbm, s) {
  const min = Number(s.sea_min_cbm);
  let best = 0;
  lines.forEach((l) => {
    const rest = seaCbm - unitMeasures(l.sku, l.qty, s).sea_cbm * l.qty;
    for (let k = 1; k <= 20000; k++) {
      if (best && k >= best) return;
      if (rest + unitMeasures(l.sku, l.qty + k, s).sea_cbm * (l.qty + k) >= min - 1e-9) { best = k; return; }
    }
  });
  return best;
}

/** The refused products' rows for a mode and class — cheapest first, no carrier name. */
export const refusedRows = (s, mode, cls) => (s.refused_rates?.[mode]?.[cls] || []).filter((r) => Number(r.rate) > 0);
function withRate(s, mode, cls, rate) {
  const k = mode === 'air' ? `air_rate_${cls}` : cls === 'general' ? 'sea_rate_cbm' : `sea_rate_${cls}`;
  return { ...s, [k]: Number(rate) };
}

/**
 * The carrier of one line in one option: 'partner' | 'refused' | 'express', or null — «اطلب سعر الشحن».
 * A line is { unit_sar, qty, sku, cls, refused, staff }.
 *   - staff-priced products ask in every option;
 *   - air never for a variant air does not suit (rule B; sea-only bulky goods);
 *   - express only up to MAX_EXPRESS_UNIT_KG chargeable per piece;
 *   - a refused product needs a refused-rate row for its class; the partner's sea needs a sea price.
 */
export function lineCarrier(l, mode, s) {
  if (l.staff) return null;
  if (mode === 'express') return unitMeasures(l.sku, 1, s).air_kg <= MAX_EXPRESS_UNIT_KG ? 'express' : null;
  if (mode === 'air' && airUnsuited(l.sku, l.cls, l.unit_sar, s)) return null;
  if (l.refused) return refusedRows(s, mode, l.cls).length ? 'refused' : null;
  if (mode === 'sea') return l.sku.sea_ok ? 'partner' : null;
  return 'partner';
}

/**
 * Why a line asks («اطلب سعر الشحن») in one option — a short reason the trader reads, never a bare «—»
 * (store test, 2026-11-03). Codes: 'staff' weight or size not trusted · 'express_limit' over the express
 * carrier's weight/size limit · 'bulky' a size-class product (bed, shelving…) air does not carry ·
 * 'heavy' air costs more than half the delivered price (rule B) · 'refused' the partner refuses it and
 * no other rate exists for this option · 'no_sea' no reliable sea price · 'express_none' express was
 * asked and gave no price. null = the line is carried by this option.
 */
export function askReason(l, mode, s) {
  if (lineCarrier(l, mode, s)) return mode === 'express' ? 'express_none' : null;
  if (l.staff) return 'staff';
  if (mode === 'express') return 'express_limit';
  if (mode === 'air' && l.sku?.air_ok === false) return 'bulky';
  if (mode === 'air' && airUnsuited(l.sku, l.cls, l.unit_sar, s)) return 'heavy';
  if (l.refused) return 'refused';
  return mode === 'sea' ? 'no_sea' : 'refused';
}

/** The lines that express would carry — the set the server is asked to quote. */
export const expressLines = (lines, s) => lines.map((l, i) => (lineCarrier(l, 'express', s) ? i : -1)).filter((i) => i >= 0);

const productsOf = (lines, idx, s) => r2(idx.reduce((a, i) => a + r2(productPrice(lines[i].unit_sar, s) * lines[i].qty), 0));

/**
 * One option at one fee rate. express (for mode 'express') is the server's answer for exactly
 * expressLines(): { lines: [i…], available, shipping_sar, days_min, days_max } — or null while
 * it is being asked (the option is then `pending`).
 * Returns { mode, available, pending, lines, ask, short, products_sar, shipping_sar, total_sar,
 * days_min, days_max, add_units, groups } — groups say how each part was priced (no carrier name:
 * 'partner', 'refused' with the row's index in refusedRows, 'express').
 */
function optionAt(lines, s, mode, rate, express) {
  const sr = { ...s, fee_rate: rate };
  const o = { mode, available: false, pending: false, lines: [], ask: [], short: [], products_sar: 0, shipping_sar: 0,
    total_sar: null, days_min: null, days_max: null, add_units: 0, groups: [] };
  const by = { partner: [], refused: [], express: [] };
  lines.forEach((l, i) => { const c = lineCarrier(l, mode, s); if (c) by[c].push(i); else o.ask.push(i); });
  let total = 0;
  const days = [];
  if (mode === 'express' && by.express.length) {
    const same = express && Array.isArray(express.lines) && express.lines.length === by.express.length && express.lines.every((i, k) => i === by.express[k]);
    if (!same) o.pending = true;
    else if (!express.available || !(Number(express.shipping_sar) > 0)) o.ask.push(...by.express);
    else {
      total += productsOf(lines, by.express, sr) + Number(express.shipping_sar);
      o.lines.push(...by.express);
      days.push([Number(express.days_min), Number(express.days_max)]);
      o.groups.push({ carrier: 'express', lines: [...by.express], shipping_sar: r2(Number(express.shipping_sar)) });
    }
  }
  if (by.partner.length) {
    const pl = by.partner.map((i) => lines[i]);
    if (mode === 'sea') {
      const cbm = pl.reduce((a, l) => a + unitMeasures(l.sku, l.qty, s).sea_cbm * l.qty, 0);
      if (cbm < Number(s.sea_min_cbm) - 1e-9) { o.short.push(...by.partner); o.add_units = piecesToSeaMin(pl, cbm, s); }
      else {
        total += pl.reduce((a, l) => a + lineLanded(l, 'sea', sr), 0);
        o.lines.push(...by.partner);
        days.push([Number(s.sea_days_min), Number(s.sea_days_max)]);
        o.groups.push({ carrier: 'partner', lines: [...by.partner], cbm: Math.round(cbm * 10000) / 10000 });
      }
    } else {
      const kg = pl.reduce((a, l) => a + unitMeasures(l.sku, l.qty, s).air_kg * l.qty, 0);
      const ship = pl.reduce((a, l) => a + r2(unitMeasures(l.sku, l.qty, s).air_kg * airRate(l.cls, s) * l.qty), 0);
      const top = kg > 0 && kg < Number(s.air_min_kg) ? r2((Number(s.air_min_kg) - kg) * (ship / kg)) : 0;
      total += pl.reduce((a, l) => a + lineLanded(l, 'air', sr), 0) + top;
      o.lines.push(...by.partner);
      days.push([Number(s.air_days_min), Number(s.air_days_max)]);
      o.groups.push({ carrier: 'partner', lines: [...by.partner], kg: Math.round(kg * 1000) / 1000, minimum_sar: top });
    }
  }
  // refused products, per class: the cheaper row for THIS cart (its minimum counted)
  for (const cls of [...new Set(by.refused.map((i) => lines[i].cls))]) {
    const idx = by.refused.filter((i) => lines[i].cls === cls);
    const gl = idx.map((i) => lines[i]);
    const measure = gl.reduce((a, l) => a + (mode === 'air' ? unitMeasures(l.sku, l.qty, s).air_kg : unitMeasures(l.sku, l.qty, s).sea_cbm) * l.qty, 0);
    let best = null;
    refusedRows(s, mode, cls).forEach((row, k) => {
      const rs = withRate(sr, mode, cls, row.rate);
      const top = measure < Number(row.min) ? r2((Number(row.min) - measure) * Number(row.rate)) : 0;
      const cost = r2(gl.reduce((a, l) => a + lineLanded(l, mode, rs), 0) + top);
      if (!best || cost < best.cost) best = { cost, k, row, top };
    });
    total += best.cost;
    o.lines.push(...idx);
    days.push([Number(best.row.days_min), Number(best.row.days_max)]);
    o.groups.push({ carrier: 'refused', class: cls, row: best.k, lines: idx, measure: Math.round(measure * 10000) / 10000, minimum_sar: best.top });
  }
  o.lines.sort((a, b) => a - b);
  o.ask.sort((a, b) => a - b);
  if (o.lines.length) {
    o.total_sar = r2(total);
    o.products_sar = productsOf(lines, o.lines, sr);
    o.shipping_sar = r2(o.total_sar - o.products_sar);
    o.days_min = Math.max(...days.map((d) => d[0]));
    o.days_max = Math.max(...days.map((d) => d[1]));
  }
  o.available = o.lines.length > 0 && !o.pending;
  return o;
}

/**
 * The option the cart opens on — the cheapest (owner, 2026-11-02), never air by default. Options can
 * carry different lines (a product with no rate asks instead of blocking), so "cheapest" is the
 * shipping paid per riyal of goods carried: the lowest shipping_sar ÷ products_sar; on a tie, the
 * option that carries more lines. An option carrying one light item cannot win by being small.
 */
export function defaultMode(c) {
  let best = null, bestK = Infinity;
  for (const m of MODES) {
    const o = c[m];
    if (!o?.available || !(o.products_sar > 0)) continue;
    const k = o.shipping_sar / o.products_sar;
    if (!best || k < bestK - 1e-9 || (Math.abs(k - bestK) <= 1e-9 && o.lines.length > best.lines.length)) { best = o; bestK = k; }
  }
  return best ? best.mode : null;
}

/**
 * The cart's three options, with the tiered fee: priced at the top rate to find the base (the higher
 * of the sea and air totals — what the trader sees), then at the base's rate.
 */
export function threeOptionCart(lines, s, express) {
  const topRate = Number(ladder(s)[0].rate);
  const sea0 = optionAt(lines, s, 'sea', topRate, null);
  const air0 = optionAt(lines, s, 'air', topRate, null);
  const base = Math.max(Number(sea0.total_sar) || 0, Number(air0.total_sar) || 0);
  const rate = feeRateFor(base, s);
  const c = {
    sea: rate === topRate ? sea0 : optionAt(lines, s, 'sea', rate, null),
    air: rate === topRate ? air0 : optionAt(lines, s, 'air', rate, null),
    express: optionAt(lines, s, 'express', rate, express),
  };
  const def = defaultMode(c);
  const top = def === 'sea' ? sea0 : def === 'air' ? air0 : null;
  const disc = top && top.total_sar != null && c[def].total_sar != null && top.lines.length === c[def].lines.length ? r2(top.total_sar - c[def].total_sar) : 0;
  return { ...c, default: def, fee_rate: rate, fee_base_sar: r2(base), fee_discount_sar: disc, next_fee_tier: nextTier(base, s) };
}
